/**
 * SignalR Context Provider for global real-time notifications.
 *
 * Maintains a single SignalR connection at the app level, preventing
 * connection churn when navigating between pages. Components subscribe
 * to events via callbacks registered with useSignalR().
 *
 * @remarks
 * This implementation follows the global connection pattern to avoid
 * WebSocket connection churn during page navigation, which was causing
 * errors and degraded performance. The provider maintains a single
 * persistent connection and uses reference counting to manage environment
 * group subscriptions.
 *
 * Architecture:
 * - Connection established once when provider mounts
 * - Automatic reconnection with exponential backoff (0s, 2s, 10s, 30s)
 * - Environment group subscriptions with reference counting
 * - Event dispatch to all registered callbacks
 * - Automatic environment group rejoin after reconnection
 *
 * Performance:
 * - Single WebSocket connection per session (not per page)
 * - Reference counting prevents unnecessary join/leave calls
 * - Event handlers use refs to avoid re-subscription on callback changes
 *
 * Security:
 * - Anonymous connections allowed in development (controlled by gateway)
 * - Production should use JWT auth via access_token query parameter
 * - Connection URL from VITE_WS_URL or VITE_API_BASE_URL environment variables
 *
 * @example
 * ```tsx
 * // In App.tsx
 * <AuthProvider>
 *   <SignalRProvider>
 *     <Routes>...</Routes>
 *   </SignalRProvider>
 * </AuthProvider>
 * ```
 *
 * @see {@link useSignalR} for accessing connection state
 * @see {@link useEnvironmentSubscription} for environment-specific subscriptions
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import * as signalR from '@microsoft/signalr'
import type { JobCreatedEvent, JobStatusUpdatedEvent } from '../api/types'
import { emitDevDiagnostic } from '../lib/devDiagnostics'

// Use WebSocket URL from environment, falling back to API base URL, then relative path
const WS_BASE_URL = import.meta.env.VITE_WS_URL || import.meta.env.VITE_API_BASE_URL || ''
const HUB_URL = `${WS_BASE_URL}/hubs/notifications`

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Event handler callback for SignalR job events.
 *
 * @param event - Either a JobCreatedEvent or JobStatusUpdatedEvent from the SignalR hub
 *
 * @see {@link JobCreatedEvent}
 * @see {@link JobStatusUpdatedEvent}
 */
export type JobEventHandler = (event: JobCreatedEvent | JobStatusUpdatedEvent) => void

/**
 * SignalR context value providing connection state and subscription API.
 *
 * @remarks
 * This interface is returned by {@link useSignalR} and provides the
 * global SignalR connection state along with the subscription API for
 * environment-specific event handlers.
 *
 * @public
 */
export interface SignalRContextValue {
  /**
   * Whether the SignalR connection is currently established.
   *
   * @remarks
   * This value changes during reconnection attempts. Components can use
   * this to show connection status UI or fall back to polling.
   */
  isConnected: boolean

  /**
   * Connection error if any.
   *
   * @remarks
   * Set when the connection fails to establish or is closed with an error.
   * Cleared on successful reconnection.
   */
  connectionError: Error | null

  /**
   * Subscribe to an environment's job events.
   *
   * @param moduleName - The environment to subscribe to (e.g., 'example', 'optimization')
   * @param onJobCreated - Optional callback invoked when a job is created
   * @param onJobStatusUpdated - Optional callback invoked when a job's status changes
   * @returns Unsubscribe function to call on component unmount
   *
   * @remarks
   * This function manages environment group subscriptions with reference counting.
   * The first subscriber to an environment causes a JoinEnvironmentGroup SignalR call.
   * The last subscriber to unsubscribe causes a LeaveEnvironmentGroup call.
   *
   * @example
   * ```tsx
   * const { subscribe } = useSignalR();
   * const queryClient = useQueryClient();
   *
   * useEffect(() => {
   *   const unsubscribe = subscribe(
   *     'example',
   *     (event) => queryClient.invalidateQueries({ queryKey: ['jobs', event.jobId] }),
   *     (event) => queryClient.invalidateQueries({ queryKey: ['jobs', event.jobId] })
   *   );
   *   return unsubscribe;
   * }, [subscribe, queryClient]);
   * ```
   */
  subscribe: (
    moduleName: string,
    onJobCreated?: (event: JobCreatedEvent) => void,
    onJobStatusUpdated?: (event: JobStatusUpdatedEvent) => void,
  ) => () => void
}

/**
 * Internal subscription record tracking environment event handlers.
 *
 * @internal
 */
interface Subscription {
  /** The environment name this subscription is for */
  moduleName: string
  /** Optional callback for job creation events */
  onJobCreated?: (event: JobCreatedEvent) => void
  /** Optional callback for job status update events */
  onJobStatusUpdated?: (event: JobStatusUpdatedEvent) => void
}

// ═══════════════════════════════════════════════════════════════════════════
// Context
// ═══════════════════════════════════════════════════════════════════════════

const SignalRContext = createContext<SignalRContextValue | null>(null)

// ═══════════════════════════════════════════════════════════════════════════
// Provider
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Props for the SignalR provider component.
 *
 * @public
 */
interface SignalRProviderProps {
  /** Child components that will have access to the SignalR context */
  children: ReactNode
}

/**
 * Provides a global SignalR connection for real-time notifications.
 *
 * @param props - Provider props containing children to render
 *
 * @remarks
 * This provider establishes a single SignalR connection when mounted and
 * maintains it for the lifetime of the application session. The connection
 * is shared by all descendant components via the {@link useSignalR} and
 * {@link useEnvironmentSubscription} hooks.
 *
 * Place this provider high in the component tree (inside AuthProvider,
 * outside routes) so the connection persists across navigation.
 *
 * Connection lifecycle:
 * 1. Provider mounts → connection established
 * 2. Connection failures → automatic reconnection with exponential backoff
 * 3. Reconnection success → all environment groups automatically rejoined
 * 4. Provider unmounts → connection gracefully closed
 *
 * @example
 * ```tsx
 * <AuthProvider>
 *   <SignalRProvider>
 *     <Routes>...</Routes>
 *   </SignalRProvider>
 * </AuthProvider>
 * ```
 *
 * @see {@link useSignalR} for accessing connection state
 * @see {@link useEnvironmentSubscription} for environment-specific subscriptions
 *
 * @public
 */
export function SignalRProvider({ children }: SignalRProviderProps) {
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<Error | null>(null)

  const connectionRef = useRef<signalR.HubConnection | null>(null)
  const subscriptionsRef = useRef<Map<string, Subscription>>(new Map())
  const environmentRefCountRef = useRef<Map<string, number>>(new Map())

  // ─────────────────────────────────────────────────────────────────────────
  // Initialize SignalR connection
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        withCredentials: true,
        // Explicit transport fallback: WebSocket → SSE → LongPolling.
        // WebKit may fail to complete WebSocket upgrade when cookies are
        // involved (ITP restrictions). SSE uses regular HTTP with cookies.
        // In production behind a load balancer, WebSocket may also be
        // unavailable — SSE fallback is critical for reliability.
        transport:
          signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents |
          signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff: 0, 2s, 10s, 30s, then 30s intervals
          if (retryContext.previousRetryCount === 0) return 0
          if (retryContext.previousRetryCount === 1) return 2000
          if (retryContext.previousRetryCount === 2) return 10000
          return 30000
        },
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build()

    // Register event handlers that dispatch to subscriptions
    connection.on('JobCreated', (event: JobCreatedEvent) => {
      subscriptionsRef.current.forEach((sub) => {
        sub.onJobCreated?.(event)
      })
      // Expose events on window for E2E tests (Playwright waitForFunction)
      const w = window as unknown as Record<string, unknown>
      const notifications = (w['jobNotifications'] as unknown[]) ?? []
      notifications.push(event)
      w['jobNotifications'] = notifications
    })

    connection.on('JobStatusUpdated', (event: JobStatusUpdatedEvent) => {
      subscriptionsRef.current.forEach((sub) => {
        sub.onJobStatusUpdated?.(event)
      })
      // Expose events on window for E2E tests (Playwright waitForFunction)
      const w = window as unknown as Record<string, unknown>
      const notifications = (w['jobNotifications'] as unknown[]) ?? []
      notifications.push(event)
      w['jobNotifications'] = notifications
    })

    // Connection state handlers
    connection.onreconnecting((error) => {
      emitDevDiagnostic('warn', 'signalr_reconnecting', { error: error?.message })
      setIsConnected(false)
      ;(window as unknown as Record<string, unknown>)['signalRConnected'] = false
    })

    connection.onreconnected(async (connectionId) => {
      emitDevDiagnostic('info', 'signalr_reconnected', { connectionId })
      setIsConnected(true)
      setConnectionError(null)
      ;(window as unknown as Record<string, unknown>)['signalRConnected'] = true

      // Rejoin all environment groups after reconnection
      for (const moduleName of environmentRefCountRef.current.keys()) {
        try {
          await connection.invoke('JoinEnvironmentGroup', moduleName)
          emitDevDiagnostic('info', 'signalr_rejoined_environment_group', { moduleName })
        } catch (err) {
          emitDevDiagnostic('error', 'signalr_rejoin_environment_group_failed', {
            moduleName,
            error: err instanceof Error ? err.message : String(err),
          })
        }
      }
    })

    connection.onclose((error) => {
      emitDevDiagnostic('warn', 'signalr_connection_closed', { error: error?.message })
      setIsConnected(false)
      if (error) {
        setConnectionError(error)
      }
    })

    // Start connection
    connection
      .start()
      .then(() => {
        emitDevDiagnostic('info', 'signalr_connected')
        setIsConnected(true)
        setConnectionError(null)
        // Expose connection state on window for E2E tests (Playwright waitForFunction)
        ;(window as unknown as Record<string, unknown>)['signalRConnected'] = true
      })
      .catch((err: Error) => {
        emitDevDiagnostic('error', 'signalr_connection_failed', { error: err.message })
        setConnectionError(err)
        ;(window as unknown as Record<string, unknown>)['signalRConnected'] = false
      })

    connectionRef.current = connection

    // Cleanup on unmount
    return () => {
      connection.stop().catch((err) => {
        emitDevDiagnostic('warn', 'signalr_stop_failed', {
          error: err instanceof Error ? err.message : String(err),
        })
      })
    }
  }, [])

  // ─────────────────────────────────────────────────────────────────────────
  // Subscribe to environment events
  // ─────────────────────────────────────────────────────────────────────────
  const subscribe = useCallback(
    (
      moduleName: string,
      onJobCreated?: (event: JobCreatedEvent) => void,
      onJobStatusUpdated?: (event: JobStatusUpdatedEvent) => void,
    ): (() => void) => {
      const subscriptionId = `${moduleName}-${Date.now()}-${Math.random()}`

      // Store subscription
      subscriptionsRef.current.set(subscriptionId, {
        moduleName,
        onJobCreated,
        onJobStatusUpdated,
      })

      // Track environment group reference count
      const currentCount = environmentRefCountRef.current.get(moduleName) ?? 0
      environmentRefCountRef.current.set(moduleName, currentCount + 1)

      // Join environment group if this is the first subscriber
      if (currentCount === 0) {
        const connection = connectionRef.current
        if (connection?.state === signalR.HubConnectionState.Connected) {
          connection
            .invoke('JoinEnvironmentGroup', moduleName)
            .then(() => {
              emitDevDiagnostic('info', 'signalr_joined_environment_group', { moduleName })
            })
            .catch((err) => {
              emitDevDiagnostic('error', 'signalr_join_environment_group_failed', {
                moduleName,
                error: err instanceof Error ? err.message : String(err),
              })
            })
        }
      }

      // Return unsubscribe function
      return () => {
        subscriptionsRef.current.delete(subscriptionId)

        // Decrement reference count
        const count = environmentRefCountRef.current.get(moduleName) ?? 1
        if (count <= 1) {
          environmentRefCountRef.current.delete(moduleName)

          // Leave environment group if no more subscribers
          const connection = connectionRef.current
          if (connection?.state === signalR.HubConnectionState.Connected) {
            connection
              .invoke('LeaveEnvironmentGroup', moduleName)
              .then(() => {
                emitDevDiagnostic('info', 'signalr_left_environment_group', { moduleName })
              })
              .catch((err) => {
                emitDevDiagnostic('error', 'signalr_leave_environment_group_failed', {
                  moduleName,
                  error: err instanceof Error ? err.message : String(err),
                })
              })
          }
        } else {
          environmentRefCountRef.current.set(moduleName, count - 1)
        }
      }
    },
    [],
  )

  const value: SignalRContextValue = {
    isConnected,
    connectionError,
    subscribe,
  }

  return <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>
}

// ═══════════════════════════════════════════════════════════════════════════
// Hook
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Access the global SignalR connection state and subscription API.
 *
 * @throws Error if used outside of SignalRProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isConnected, subscribe } = useSignalR();
 *   const queryClient = useQueryClient();
 *
 *   useEffect(() => {
 *     const unsubscribe = subscribe(
 *       'example',
 *       (event) => queryClient.invalidateQueries({ queryKey: ['jobs', event.jobId] }),
 *       (event) => queryClient.invalidateQueries({ queryKey: ['jobs', event.jobId] })
 *     );
 *     return unsubscribe;
 *   }, [subscribe, queryClient]);
 *
 *   return <div>Connected: {isConnected ? 'Yes' : 'No'}</div>;
 * }
 * ```
 */
export function useSignalR(): SignalRContextValue {
  const context = useContext(SignalRContext)
  if (!context) {
    throw new Error('useSignalR must be used within a SignalRProvider')
  }
  return context
}

/**
 * Convenience hook that subscribes to an environment's job events.
 *
 * @param moduleName - The environment to subscribe to (e.g., 'example', 'optimization')
 * @param onJobCreated - Optional callback invoked when a job is created for this environment
 * @param onJobStatusUpdated - Optional callback invoked when a job's status changes
 * @returns Object containing `isConnected` boolean indicating SignalR connection state
 *
 * @remarks
 * This hook automatically handles the subscription lifecycle tied to component
 * mount/unmount. It's the recommended way for environment pages to receive
 * real-time notifications.
 *
 * The hook uses refs internally to keep callbacks stable, preventing
 * re-subscription on every render when callback functions change.
 *
 * Environment group subscription is managed with reference counting. The first
 * component to subscribe to an environment causes a JoinEnvironmentGroup call.
 * When the last component unsubscribes, LeaveEnvironmentGroup is called.
 *
 * Common pattern: Use `onJobStatusUpdated` to invalidate React Query caches
 * when jobs complete, eliminating the need for polling.
 *
 * @example
 * ```tsx
 * function ExampleEnvironmentPage() {
 *   const queryClient = useQueryClient();
 *
 *   // Subscribe to job status updates, invalidate cache on completion
 *   const { isConnected } = useEnvironmentSubscription(
 *     'example',
 *     undefined, // No onJobCreated handler needed
 *     (event) => {
 *       if (event.status === 'completed' || event.status === 'failed') {
 *         queryClient.invalidateQueries({ queryKey: ['training-runs'] });
 *       }
 *     }
 *   );
 *
 *   // Use isConnected to show connection status or fall back to polling
 *   const { data } = useQuery({
 *     queryKey: ['training-runs'],
 *     queryFn: fetchRuns,
 *     refetchInterval: isConnected ? false : 5000, // Poll only if disconnected
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 *
 * @see {@link useSignalR} for lower-level access to the connection
 *
 * @public
 */
export function useEnvironmentSubscription(
  moduleName: string,
  onJobCreated?: (event: JobCreatedEvent) => void,
  onJobStatusUpdated?: (event: JobStatusUpdatedEvent) => void,
): { isConnected: boolean } {
  const { isConnected, subscribe } = useSignalR()

  // Keep callbacks in refs to avoid re-subscribing on every render
  const onJobCreatedRef = useRef(onJobCreated)
  const onJobStatusUpdatedRef = useRef(onJobStatusUpdated)

  useEffect(() => {
    onJobCreatedRef.current = onJobCreated
    onJobStatusUpdatedRef.current = onJobStatusUpdated
  }, [onJobCreated, onJobStatusUpdated])

  useEffect(() => {
    const unsubscribe = subscribe(
      moduleName,
      (event) => onJobCreatedRef.current?.(event),
      (event) => onJobStatusUpdatedRef.current?.(event),
    )
    return unsubscribe
  }, [moduleName, subscribe])

  return { isConnected }
}
